import React, { useState, useEffect } from 'react';
import { Clock, CheckCircle2, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';

export default function VerificationCountdown({ fechaVerificacionInicio, estado }) {
  const [timeRemaining, setTimeRemaining] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    if (!fechaVerificacionInicio || estado === 'aprobado') {
      return;
    }

    const calculateTimeRemaining = () => {
      const startDate = new Date(fechaVerificacionInicio);
      const now = new Date();
      const endDate = new Date(startDate.getTime() + 72 * 60 * 60 * 1000); // 72 horas después
      
      const diff = endDate - now;
      
      if (diff <= 0) {
        setIsExpired(true);
        setTimeRemaining({ hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setTimeRemaining({ hours, minutes, seconds });
    };

    calculateTimeRemaining();
    const interval = setInterval(calculateTimeRemaining, 1000);

    return () => clearInterval(interval);
  }, [fechaVerificacionInicio, estado]);

  if (!fechaVerificacionInicio || estado !== 'en_verificacion') {
    return null;
  }

  const progress = (() => {
    const startDate = new Date(fechaVerificacionInicio);
    const now = new Date();
    const totalMs = 72 * 60 * 60 * 1000;
    const elapsedMs = now - startDate;
    return Math.min(100, (elapsedMs / totalMs) * 100);
  })();

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-[#2A2A2A] to-[#1A1A1A] border-2 border-[#F5A623]/30 rounded-2xl p-6 mb-6"
      data-testid="verification-countdown-admin"
    >
      <div className="flex items-center gap-3 mb-4">
        {isExpired ? (
          <AlertCircle className="w-6 h-6 text-[#EF4444]" />
        ) : (
          <Clock className="w-6 h-6 text-[#F5A623]" />
        )}
        <h3 className="text-xl font-heading font-bold text-white">
          {isExpired ? '⏰ Período de verificación finalizado' : '⏱️ Verificación en curso'}
        </h3>
      </div>

      {isExpired ? (
        <div className="p-4 bg-[#EF4444]/20 border border-[#EF4444]/30 rounded-lg">
          <p className="text-[#EF4444] font-semibold">
            Las 72 horas han transcurrido. El sistema aprobará automáticamente este beneficiario.
          </p>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-center gap-6 mb-6">
            <div className="text-center">
              <div className="text-4xl font-black text-[#F5A623] font-mono">
                {String(timeRemaining.hours).padStart(2, '0')}
              </div>
              <div className="text-xs text-[#666666] uppercase mt-1">Horas</div>
            </div>
            <div className="text-3xl font-black text-[#F5A623]">:</div>
            <div className="text-center">
              <div className="text-4xl font-black text-[#F5A623] font-mono">
                {String(timeRemaining.minutes).padStart(2, '0')}
              </div>
              <div className="text-xs text-[#666666] uppercase mt-1">Minutos</div>
            </div>
            <div className="text-3xl font-black text-[#F5A623]">:</div>
            <div className="text-center">
              <div className="text-4xl font-black text-[#F5A623] font-mono">
                {String(timeRemaining.seconds).padStart(2, '0')}
              </div>
              <div className="text-xs text-[#666666] uppercase mt-1">Segundos</div>
            </div>
          </div>

          {/* Barra de progreso */}
          <div className="mb-4">
            <div className="flex justify-between text-xs text-[#666666] mb-2">
              <span>Inicio</span>
              <span>{progress.toFixed(1)}% transcurrido</span>
              <span>72h</span>
            </div>
            <div className="w-full bg-[#2A2A2A] rounded-full h-2 overflow-hidden">
              <motion.div
                className="bg-gradient-to-r from-[#10B981] via-[#F5A623] to-[#EF4444] h-2"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
          </div>

          <div className="flex items-start gap-2 p-3 bg-[#3B82F6]/10 border border-[#3B82F6]/30 rounded-lg">
            <CheckCircle2 className="w-5 h-5 text-[#3B82F6] flex-shrink-0 mt-0.5" />
            <p className="text-sm text-[#666666]">
              Al finalizar las 72 horas, el beneficiario será <span className="text-[#10B981] font-semibold">aprobado automáticamente</span> y recibirá una notificación por correo.
            </p>
          </div>
        </>
      )}
    </motion.div>
  );
}
